import {Button} from '@mantine/core';
import React, {useCallback, useEffect} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ContentStoreModal from 'extensions/content-store/ContentStoreModal';
import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {
  toAvailableConnectorListItem,
  toAwaitingSetupConnectorListItem,
  toConnectedConnectorListItem,
} from 'llm/chat-library/connectors/connectorsModalAdapter';
import useConnectors from 'llm/chat-library/connectors/useConnectors';
import ChatSettingsListingView from 'llm/chat-library/shared/list-detail/ChatSettingsListingView';
import type {ListGroup} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import type {MCPServerConfig, MCPServerUserConfig} from 'llm/chat-library/sidepanel/AISettingsConnectors';
import {
  closeChatSettingsModal,
  ConnectorsGroup,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {MCP_PRODUCT_NAME_PLURAL} from 'llm/NamingConstants';
import ConnectMCPModal from 'nav/sidepanel/mcps/ConnectMCPModal';
import UserToolPermissionsModal from 'nav/sidepanel/mcps/UserMcpConfigurationModal';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';
import usePreference from 'ui-library/hooks/usePreference';
import legacyRunModal from 'ui-library/legacyRunModal';
import {deletePromise, isServerRequestError} from 'util/Net';

/** Where admins finish configuring installed connectors that are still awaiting tool access. */
const TAC_MCP_URL = '/admin/settings#ai-connectors';

/**
 * The Connectors tab's groups, in display order. `id` is the tab value and modal-state group key;
 * `label` is the tab text. The listing view derives its tab bar from these.
 */
const CONNECTOR_GROUP_ORDER: Array<{id: ConnectorsGroup; label: string}> = [
  {id: ConnectorsGroup.AVAILABLE, label: 'Available'},
  {id: ConnectorsGroup.CONNECTED, label: 'Connected'},
  {id: ConnectorsGroup.AWAITING_SETUP, label: 'Awaiting setup'},
];

/**
 * Displays the AI Connectors tab in the chat settings modal: group tabs for Available / Connected /
 * Awaiting setup, each row rendered by the shared listing view. Connectors have no detail page, so
 * every action lives in the row (Connect button, status badge, manage/disconnect menu) rather than a
 * detail panel. Mirrors ChatSettingsSkillContent's shape.
 */
export default function ChatSettingsConnectorContent({
  group,
  onGroupChange,
}: {
  group: ConnectorsGroup;
  onGroupChange: (group: ConnectorsGroup) => void;
}): JSX.Element {
  const {connectedConfigs, availableConfigs, awaitingSetupConfigs, isLoading, isErrored, refetch} =
    useConnectors();
  const [showCopyApiId] = usePreference<boolean | undefined>('ENABLE_COPY_API_ID');

  useEffect(() => {
    if (isErrored) {
      notifications.error(`Could not load ${MCP_PRODUCT_NAME_PLURAL}; try reloading the page.`);
    }
  }, [isErrored]);

  // TODO(nweeden): Replace legacyRunModal with runModal once ConnectMCPModal and UserToolPermissionsModal
  // are migrated to the `close` prop interface. They currently use onRequestClose/onRequestSubmit
  // and are shared across the legacy AI settings, PlusMenu, and the TAC.
  const handleConnect = useCallback(
    (config: MCPServerConfig) => {
      legacyRunModal(ConnectMCPModal, {
        config,
        onRequestSubmit: (userConfigId: string) => {
          refetch();
          legacyRunModal(UserToolPermissionsModal, {
            userConfigId,
            serverId: config.id,
            serverName: config.name,
            onRequestSubmit: () => {
              refetch();
            },
          });
        },
      });
    },
    [refetch]
  );

  const handleDisconnect = useCallback(
    async (userConfig: MCPServerUserConfig) => {
      try {
        await deletePromise({url: `/1/api/llm/mcp-server-user-configs/${userConfig.id}`});
        notifications.success(`${userConfig.mcpServerConfig.name} disconnected successfully`);
        refetch();
      } catch (e) {
        if (isServerRequestError(e)) {
          notifications.error(`Could not disconnect ${userConfig.mcpServerConfig.name}`);
        } else {
          throw e;
        }
      }
    },
    [refetch]
  );

  const handleManageTools = useCallback(
    (userConfig: MCPServerUserConfig) => {
      legacyRunModal(UserToolPermissionsModal, {
        userConfigId: userConfig.id,
        serverId: userConfig.mcpServerConfig.id,
        serverName: userConfig.mcpServerConfig.name,
        onRequestSubmit: () => {
          refetch();
        },
      });
    },
    [refetch]
  );

  const handleBrowseDirectory = useCallback(() => {
    legacyRunModal(ContentStoreModal, {
      extensionType: ExtensionType.MCP_SERVER,
      onInstall: refetch,
    });
  }, [refetch]);

  const groups: Array<ListGroup> = CONNECTOR_GROUP_ORDER.map(({id, label}) => {
    let items: ListGroup['items'];
    switch (id) {
      case ConnectorsGroup.AVAILABLE:
        items = availableConfigs.map((config) =>
          toAvailableConnectorListItem(config, {onConnect: handleConnect})
        );
        break;
      case ConnectorsGroup.CONNECTED:
        items = connectedConfigs.map((userConfig) =>
          toConnectedConnectorListItem(
            userConfig,
            {onManageTools: handleManageTools, onDisconnect: handleDisconnect},
            {showCopyApiId: Boolean(showCopyApiId)}
          )
        );
        break;
      case ConnectorsGroup.AWAITING_SETUP:
        items = awaitingSetupConfigs.map((config) => toAwaitingSetupConnectorListItem(config));
        break;
      default:
        items = [];
    }
    return {id, header: label, items};
  });

  const isTenantAdmin = Boolean(EntityStore.currentUser()?.isTenantAdmin);
  // Non-admins on tenants with the directory can browse/install more connectors; admins instead get a
  // link to finish tool-access setup, shown on the Awaiting setup group where that work applies.
  const showDirectoryButton = BenchlingConfig.ENABLE_EXTENSION_DIRECTORY && !isTenantAdmin;
  let headerAction: React.ReactNode;
  if (group === ConnectorsGroup.AWAITING_SETUP && isTenantAdmin) {
    headerAction = (
      <Button
        variant="secondary"
        size="xs"
        onClick={() => {
          closeChatSettingsModal();
          navigateToURL(TAC_MCP_URL);
        }}
      >
        Configure in Admin Settings
      </Button>
    );
  } else if (showDirectoryButton) {
    headerAction = (
      <Button variant="secondary" size="xs" onClick={handleBrowseDirectory}>
        Browse directory
      </Button>
    );
  }

  return (
    <ChatSettingsListingView
      header={{
        title: MCP_PRODUCT_NAME_PLURAL,
        action: headerAction,
      }}
      groups={groups}
      activeGroupId={group}
      onGroupChange={(value) => {
        onGroupChange(value as ConnectorsGroup);
      }}
      isLoading={isLoading}
      emptyMessage={`No ${MCP_PRODUCT_NAME_PLURAL} to show.`}
    />
  );
}
